import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useState, useEffect } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import { createAdvancedRule, updateAdvancedRule } from '../../api/preferencesApi';
import { AppSelect, AppInput, colorTokens } from '@eruvaka/web-pondlogs-base';
import { useTranslation } from '@eruvaka/web-pondlogs-i18n';
const OPERATORS = ['>', '<', '>=', '<=', '=', '!=', 'BETWEEN'];
const PARAMETERS = [
    'pH',
    'Temperature',
    'DO',
    'Salinity',
    'Alkalinity',
    'Hardness',
    'TAN / NH4',
    'Ca',
    'Mg',
    'K / Kali',
    'Fe',
    'NO2',
    'NTU',
    'Secchi Disk',
    'Vibrio',
    'V. Parahaemolyticus',
    'V. Alginolyticus',
    'Total Bacteria Count',
    'Green Algae',
    'Blue Green Algae',
    'Diatom',
    'Feed Quantity',
    'ABW',
    'AWG',
    'Medicine Quantity',
];
const emptyCondition = () => ({
    parameter: 'pH',
    operator: '>',
    value: null,
    minValue: null,
    maxValue: null,
    logicalOperator: 'AND',
});
export const RuleFormDialog = memo((props) => {
    const { open, rule, onClose } = props;
    const { t } = useTranslation('main');
    const isEdit = !!rule;
    const [description, setDescription] = useState('');
    const [conditions, setConditions] = useState([emptyCondition()]);
    const [confirmationMessage, setConfirmationMessage] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (open) {
            if (rule) {
                setDescription(rule.description);
                setConditions(rule.conditions.map((c) => ({ ...c })));
                setConfirmationMessage(rule.confirmationMessage);
            }
            else {
                setDescription('');
                setConditions([emptyCondition()]);
                setConfirmationMessage('');
            }
            setError(null);
        }
    }, [open, rule]);
    const updateCond = (index, patch) => setConditions((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
    const validate = () => {
        if (!description.trim())
            return 'Rule name is required.';
        if (!conditions.length)
            return 'At least one condition is required.';
        for (const c of conditions) {
            if (!c.parameter)
                return 'All conditions must have a parameter.';
            if (c.operator === 'BETWEEN') {
                if (c.minValue === null || c.maxValue === null)
                    return 'Both min and max required for BETWEEN.';
                if (Number(c.minValue) >= Number(c.maxValue))
                    return 'Min must be less than max for BETWEEN.';
            }
            else if (c.value === null) {
                return 'Condition value is required.';
            }
        }
        if (!confirmationMessage.trim())
            return 'Confirmation message is required.';
        return null;
    };
    const handleSave = async () => {
        const err = validate();
        if (err) {
            setError(err);
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const payload = {
                description: description.trim(),
                conditions,
                confirmationMessage: confirmationMessage.trim(),
            };
            if (isEdit && rule) {
                await updateAdvancedRule(rule.ruleId, payload);
            }
            else {
                await createAdvancedRule(payload);
            }
            onClose(true);
        }
        catch (e) {
            setError('errors.saveRuleFailed');
        }
        finally {
            setSaving(false);
        }
    };
    return (_jsxs(Dialog, { open: open, onClose: () => onClose(false), maxWidth: "sm", fullWidth: true, children: [_jsxs(DialogTitle, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: 14,
                    fontWeight: 700,
                    color: '#1a2233',
                    textTransform: 'none',
                }, children: [isEdit ? 'Edit monitoring rule' : 'New monitoring rule', _jsx(IconButton, { size: "small", onClick: () => onClose(false), children: _jsx(CloseIcon, { sx: { fontSize: 16, color: colorTokens.darkGrey } }) })] }), _jsxs(DialogContent, { dividers: true, children: [error && (_jsx(Alert, { severity: "error", sx: { mb: 2 }, onClose: () => setError(null), children: error && t(error) })), _jsxs(Stack, { spacing: 3, children: [_jsxs(Box, { children: [_jsxs(Typography, { sx: { fontSize: 12, fontWeight: 600, color: colorTokens.primary, mb: 0.75 }, children: ["Rule Name ", _jsx("span", { style: { color: colorTokens.error }, children: "*" })] }), _jsx(AppInput, { value: description, onChange: (e) => setDescription(e.target.value), placeholder: "e.g. Low DO Alert" })] }), _jsxs(Box, { children: [_jsxs(Box, { sx: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            mb: 1.5,
                                        }, children: [_jsx(Typography, { sx: { fontSize: 12, fontWeight: 600, color: colorTokens.primary }, children: "Conditions *" }), _jsx(Button, { startIcon: _jsx(AddIcon, {}), size: "small", onClick: () => setConditions((p) => [...p, emptyCondition()]), sx: { fontSize: 11, color: colorTokens.magenta, textTransform: 'none' }, children: "Add Condition" })] }), _jsx(Stack, { spacing: 1.5, children: conditions.map((cond, i) => (_jsxs(Box, { children: [i > 0 && (_jsx(Box, { sx: { display: 'flex', gap: 0.75, mb: 1 }, children: ['AND', 'OR'].map((l) => (_jsx(Button, { size: "small", variant: cond.logicalOperator === l ? 'contained' : 'outlined', onClick: () => updateCond(i, { logicalOperator: l }), sx: {
                                                            fontSize: 10,
                                                            minWidth: 48,
                                                            py: 0.25,
                                                            borderRadius: 10,
                                                            fontWeight: 700,
                                                            background: cond.logicalOperator === l ? colorTokens.primary : 'transparent',
                                                            borderColor: cond.logicalOperator === l
                                                                ? colorTokens.primary
                                                                : colorTokens.borderLight,
                                                            color: cond.logicalOperator === l ? 'white' : colorTokens.darkGrey,
                                                            textTransform: 'none',
                                                            '&:hover': {
                                                                background: cond.logicalOperator === l ? '#002d55' : colorTokens.lightGrey,
                                                            },
                                                        }, children: l }, l))) })), _jsxs(Box, { sx: {
                                                        display: 'flex',
                                                        gap: 1,
                                                        alignItems: 'center',
                                                        p: 1.5,
                                                        borderRadius: 2,
                                                        background: colorTokens.lightGrey,
                                                    }, children: [_jsx(AppSelect, { value: cond.parameter, onChange: (v) => updateCond(i, { parameter: String(v) }), options: PARAMETERS.map((p) => ({ value: p, label: p })), sx: { flex: 1 } }), _jsx(AppSelect, { value: cond.operator, onChange: (v) => updateCond(i, {
                                                                operator: v,
                                                                value: null,
                                                                minValue: null,
                                                                maxValue: null,
                                                            }), options: OPERATORS.map((op) => ({ value: op, label: op })), sx: { flex: 1 } }), cond.operator === 'BETWEEN' ? (_jsxs(_Fragment, { children: [_jsx(AppInput, { type: "number", placeholder: "Min", value: cond.minValue ?? '', onChange: (e) => updateCond(i, {
                                                                        minValue: e.target.value ? parseFloat(e.target.value) : null,
                                                                    }), sx: { flex: 1 }, inputProps: { step: 'any' } }), _jsx(AppInput, { type: "number", placeholder: "Max", value: cond.maxValue ?? '', onChange: (e) => updateCond(i, {
                                                                        maxValue: e.target.value ? parseFloat(e.target.value) : null,
                                                                    }), sx: { flex: 1 }, inputProps: { step: 'any' } })] })) : (_jsx(AppInput, { type: "number", placeholder: "Value", value: cond.value ?? '', onChange: (e) => updateCond(i, {
                                                                value: e.target.value ? parseFloat(e.target.value) : null,
                                                            }), sx: { flex: 1 }, inputProps: { step: 'any' } })), conditions.length > 1 && (_jsx(IconButton, { size: "small", onClick: () => setConditions((p) => p.filter((_, j) => j !== i)), "aria-label": "remove condition", children: _jsx(DeleteOutlineIcon, { sx: { fontSize: 16, color: '#C8102E' } }) }))] })] }, i))) })] }), _jsxs(Box, { children: [_jsxs(Typography, { sx: { fontSize: 12, fontWeight: 600, color: colorTokens.primary, mb: 0.75 }, children: ["Confirmation Message ", _jsx("span", { style: { color: colorTokens.error }, children: "*" })] }), _jsx(AppInput, { value: confirmationMessage, onChange: (e) => setConfirmationMessage(e.target.value), multiline: true, rows: 2, placeholder: "Message shown when this rule triggers\u2026" })] })] })] }), _jsxs(DialogActions, { sx: { px: 3, py: 2, gap: 1 }, children: [_jsx(Button, { onClick: () => onClose(false), disabled: saving, variant: "outlined", sx: {
                            fontSize: 11,
                            borderColor: colorTokens.borderLight,
                            color: colorTokens.darkGrey,
                            textTransform: 'none',
                            borderRadius: 2,
                        }, children: "Cancel" }), _jsx(Button, { onClick: handleSave, variant: "contained", disabled: saving, startIcon: saving ? _jsx(CircularProgress, { size: 12, color: "inherit" }) : undefined, sx: {
                            fontSize: 11,
                            background: colorTokens.primary,
                            borderRadius: 2,
                            textTransform: 'none',
                            '&:hover': { background: '#002d55' },
                        }, children: isEdit ? 'Save Changes' : 'Create Rule' })] })] }));
});
RuleFormDialog.displayName = 'RuleFormDialog';
